import React, { useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  className?: string;
}

export function PullToRefresh({ onRefresh, children, className = '' }: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { pullDistance, isRefreshing, isTriggered } = usePullToRefresh({
    onRefresh,
    containerRef,
  });

  const indicatorVisible = pullDistance > 0 || isRefreshing;
  const indicatorOffset = isRefreshing ? 48 : pullDistance;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Pull indicator */}
      <div
        aria-hidden="true"
        className="pull-indicator-top"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: `${indicatorOffset}px`,
          overflow: 'hidden',
          transition: isRefreshing ? 'height 0.2s ease' : 'none',
          pointerEvents: 'none',
          zIndex: 49,
        }}
      >
        {indicatorVisible && (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'hsl(var(--background, 0 0% 100%))',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.15s ease, opacity 0.15s ease',
              opacity: Math.min(pullDistance / 48, 1),
            }}
          >
            <RefreshCw
              className={`h-5 w-5 text-theme-primary ${isRefreshing ? 'animate-spin' : ''}`}
              style={{
                transform: isRefreshing ? undefined : `rotate(${(pullDistance / 72) * 180}deg)`,
                transition: 'transform 0.1s linear',
                color: isTriggered ? 'var(--color-theme-primary, #4A1D96)' : '#9CA3AF',
              }}
            />
          </div>
        )}
      </div>

      {/* Content shifted down while pulling */}
      <div
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: pullDistance > 0 ? 'none' : 'transform 0.25s ease',
          willChange: pullDistance > 0 || isRefreshing ? 'transform' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}
